import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, AlertCircle, Clock, Gift, Settings, Upload,
  Package, ArrowLeft, ArrowRight, X, LogOut, Trash2, Image as ImageIcon,
  Globe, Copy, ExternalLink,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';
import ServiceAreaAndVideo from './ServiceAreaAndVideo';
import AudioTracks from './AudioTracks';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import EthnicityMultiSelect from '@/components/EthnicityMultiSelect';
import { toEthnicityArray, hasIndigenous } from '@/utils/ethnicity';
import useAutoSave from '@/hooks/useAutoSave';
import AutoSaveIndicator from '@/components/AutoSaveIndicator';
import { publicUrl } from '@/lib/publicUrl';

const INDIGENOUS_GROUPS = new Set([
  'Native American',
  'Alaska Native',
  'First Nations',
  'Métis',
  'Inuit',
  'Native Hawaiian',
]);

/**
 * Shows the vendor their own publicly-shareable profile URL (no login required to view).
 * This is what they paste into Stripe Connect's "Business Website" field if they don't
 * have their own domain — Stripe reviewers can visit it directly to verify services/products.
 */
function PublicProfileUrlCard({ vendor }) {
  const vendorId = vendor?.vendor_id;
  if (!vendorId) return null;
  const url = publicUrl(`/vendor/${vendorId}`);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('Public profile URL copied'); }
    catch { toast.error('Could not copy'); }
  };
  return (
    <GlassCard className="p-6" data-testid="public-profile-url-card">
      <h2 className="text-lg font-semibold text-white font-syne mb-1 flex items-center gap-2">
        <Globe size={18} className="text-emerald-300" /> Your Public Profile URL
      </h2>
      <p className="text-[#9CA3AF] text-sm mb-4 leading-relaxed">
        Anyone can view this page — <strong className="text-white">no account or login required</strong>. Share it on social media, with clients,
        or paste it into Stripe's <em>"Business website"</em> field if you don't have a personal domain.
        Stripe reviewers (and your customers) will see your services, products, photos, and contact options.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 bg-[#0E0E23] border border-emerald-400/30 rounded-xl px-4 py-3 font-mono text-sm text-emerald-200 truncate" data-testid="public-profile-url">
          {url}
        </div>
        <button
          onClick={copy}
          className="px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold inline-flex items-center gap-2"
          data-testid="copy-public-url-btn"
        >
          <Copy size={14} /> Copy
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-600 hover:from-fuchsia-400 hover:to-violet-500 text-white text-sm font-bold inline-flex items-center gap-2"
          data-testid="open-public-url-btn"
        >
          <ExternalLink size={14} /> Preview
        </a>
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
          <p className="text-emerald-300 font-bold mb-1">✓ Public</p>
          <p className="text-[#9CA3AF]">No login required to view — anyone with the link can browse.</p>
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
          <p className="text-emerald-300 font-bold mb-1">✓ Stripe-friendly</p>
          <p className="text-[#9CA3AF]">Paste in your Stripe Connect "Business website" field if you don't have a domain.</p>
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
          <p className="text-emerald-300 font-bold mb-1">✓ Booking-ready</p>
          <p className="text-[#9CA3AF]">Visitors are prompted to sign up only when they click Book / Message.</p>
        </div>
      </div>
    </GlassCard>
  );
}

export default function EditProfile() {
  const navigate = useNavigate();
  const { getAuthHeaders, logout, user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusLoading, setStatusLoading] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [bio, setBio] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [location, setLocation] = useState('');
  const [ethnicity, setEthnicity] = useState([]);
  const [tribalAffiliation, setTribalAffiliation] = useState('');
  const [einNumber, setEinNumber] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);

  // Auto-save: only fire after the initial GET hydrates state, otherwise the
  // hook will re-PUT the just-loaded values immediately on mount.
  const loadedOnce = useRef(false);
  const lastSavedSnapshot = useRef(null);

  // Promo code auto-save (separate endpoint — /api/users/profile)
  const [promoCode, setPromoCode] = useState('');
  const promoLoadedOnce = useRef(false);

  useEffect(() => {
    if (user?.promo_code) {
      setPromoCode(user.promo_code);
      // mark loaded so we don't echo the same value back as a save
      setTimeout(() => { promoLoadedOnce.current = true; }, 0);
    } else {
      promoLoadedOnce.current = true;
    }
  }, [user?.promo_code]);

  useEffect(() => {
    fetchVendor();
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const response = await fetch(`${API}/stripe/connect/status`, { headers: getAuthHeaders() });
      if (response.ok) setStripeStatus(await response.json());
    } catch {}
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await fetch(`${API}/stripe/connect/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start Stripe verification');
      }
    } catch {
      toast.error('Failed to connect Stripe');
    } finally {
      setConnectingStripe(false);
    }
  };

  const fetchVendor = async () => {
    try {
      const response = await fetch(`${API}/vendors/me`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setVendor(data);
        setBusinessName(data.business_name || '');
        setDescription(data.description || '');
        setBio(data.bio || '');
        setYoutubeUrl(data.youtube_url || '');
        setLocation(data.location || '');
        setEthnicity(toEthnicityArray(data.ethnicity || user?.ethnicity));
        setTribalAffiliation(data.tribal_affiliation || '');
        setEinNumber(data.ein_number || '');
        setAge(data.age ? String(data.age) : '');
        setGender(data.gender || '');
        // Mark form as fully hydrated so auto-save doesn't echo a no-op PUT
        // immediately after page load. Defer one tick so the state setters
        // above flush before the auto-save effect inspects the value.
        setTimeout(() => { loadedOnce.current = true; }, 0);
      }
    } catch (error) {
      console.error('Failed to fetch vendor:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Auto-save: debounced background PUT /api/vendors/me ───
  // Builds a payload from all editable Business Info fields. Fires 1.2s after
  // the user stops typing. Sits on top of the existing manual "Save Changes"
  // button so a user can still force-save instantly.
  const autoSavePayload = useMemo(() => {
    const payload = {
      business_name: businessName,
      description,
      bio,
      youtube_url: youtubeUrl || null,
      location,
      tribal_affiliation: hasIndigenous(ethnicity) ? (tribalAffiliation || null) : null,
      age: age ? Number(age) : null,
      gender: gender || null,
    };
    // Only include ethnicity when the user has actually picked something;
    // sending [] would wipe a previously-saved selection.
    if (ethnicity && ethnicity.length > 0) payload.ethnicity = ethnicity;
    // Only include EIN when it's a valid 9-digit format — partial typing
    // otherwise raises a 400 from the backend.
    if (einNumber) {
      const cleaned = einNumber.replace(/-/g, '').trim();
      if (/^\d{9}$/.test(cleaned)) payload.ein_number = einNumber.trim();
    }
    return payload;
  }, [businessName, description, bio, youtubeUrl, location, ethnicity, tribalAffiliation, age, gender, einNumber]);

  const profileSave = useAutoSave(
    autoSavePayload,
    async (val) => {
      if (!loadedOnce.current) return;            // wait for initial hydration
      const snapshot = JSON.stringify(val);
      if (lastSavedSnapshot.current === snapshot) return;  // no-op, skip
      const r = await fetch(`${API}/vendors/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(val),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || 'Save failed');
      }
      const data = await r.json().catch(() => null);
      lastSavedSnapshot.current = snapshot;
      if (data) setVendor(data);
    },
    { delay: 1200, skipFirst: true },
  );

  // Promo code auto-save — uses /api/users/profile, debounced separately so
  // the indicator on the main form doesn't flicker when the promo field
  // changes.
  const promoSave = useAutoSave(
    promoCode,
    async (val) => {
      if (!promoLoadedOnce.current) return;
      const code = (val || '').trim();
      if (code && code.length < 4) return;        // backend rejects < 4 chars
      const r = await fetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ promo_code: code }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || 'Save failed');
      }
    },
    { delay: 1200, skipFirst: true },
  );

  const handleSave = async () => {
    if (!ethnicity || ethnicity.length === 0) {
      toast.error('Please choose at least one ethnic background before saving — it is required to list services.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        business_name: businessName,
        description,
        bio,
        youtube_url: youtubeUrl || null,
        location,
        ethnicity,
        tribal_affiliation: hasIndigenous(ethnicity) ? (tribalAffiliation || null) : null,
        age: age ? Number(age) : null,
        gender: gender || null,
      };
      const response = await fetch(`${API}/vendors/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success('Profile updated!');
        setVendor(data);
      } else {
        toast.error(data?.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/users/upload-media?media_type=profile_picture`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setVendor((prev) => ({ ...prev, profile_image: data.url }));
        toast.success('Profile picture updated!');
      } else throw new Error('Upload failed');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/users/upload-media?media_type=banner_image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setVendor((prev) => ({ ...prev, banner_image: data.url }));
        toast.success('Banner image updated!');
      } else {
        toast.error(data?.detail || 'Failed to upload banner');
      }
    } catch (error) {
      toast.error('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handlePortfolioUpload = async (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/vendors/me/portfolio?media_type=${mediaType}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`${mediaType === 'image' ? 'Image' : 'Video'} uploaded!`);
        fetchVendor();
      } else {
        toast.error(data?.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePortfolioMedia = async (mediaId) => {
    if (!mediaId) {
      toast.error('Invalid media');
      return;
    }
    try {
      const response = await fetch(`${API}/vendors/me/portfolio/${mediaId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Media deleted');
        fetchVendor();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePortfolioReorder = async (newImages) => {
    try {
      await fetch(`${API}/vendors/me/portfolio/reorder`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: newImages }),
      });
      setVendor((prev) => ({ ...prev, portfolio_images: newImages }));
    } catch {}
  };

  const movePortfolioImage = (index, direction) => {
    const imgs = [...(vendor?.portfolio_images || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= imgs.length) return;
    [imgs[index], imgs[newIndex]] = [imgs[newIndex], imgs[index]];
    handlePortfolioReorder(imgs);
  };

  const extractMediaId = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/users/delete-account`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success('Account deleted');
        await logout();
        navigate('/');
      } else throw new Error('Failed to delete account');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const setStatus = async (action) => {
    setStatusLoading(action);
    try {
      const res = await fetch(`${API}/vendors/me/toggle-status`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message);
        fetchVendor();
      } else toast.error(d.detail);
    } catch {
      toast.error('Failed');
    } finally {
      setStatusLoading('');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-12 bg-white/5 rounded-2xl animate-pulse"></div>
        <div className="h-48 bg-white/5 rounded-2xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="edit-profile-title">
            Edit Profile
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Update your vendor profile information — changes save automatically</p>
        </div>
        <div className="mt-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <AutoSaveIndicator status={profileSave.status} lastSaved={profileSave.lastSaved} />
        </div>
      </div>

      {/* Public Profile URL — visible to everyone (no login required). Paste this in Stripe if you don't have your own website. */}
      <PublicProfileUrlCard vendor={vendor} />

      {/* Profile Status Toggle */}
      <GlassCard className="p-6" data-testid="profile-status-card">
        <h2 className="text-lg font-semibold text-white font-syne mb-4 flex items-center gap-2">
          <Shield size={18} className="text-[#A78BFA]" /> Profile Status
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setStatus('activate')}
            disabled={statusLoading === 'activate'}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${vendor?.is_active && !vendor?.out_of_commission ? 'bg-green-500/20 border-2 border-green-500/40 text-green-400' : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:border-green-500/30'}`}
            data-testid="status-active"
          >
            <CheckCircle size={16} /> Active
          </button>
          <button
            onClick={() => setStatus('out_of_commission')}
            disabled={statusLoading === 'ooc'}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${vendor?.out_of_commission ? 'bg-[#F59E0B]/20 border-2 border-[#F59E0B]/40 text-[#FCD34D]' : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:border-[#F59E0B]/30'}`}
            data-testid="status-ooc"
          >
            <Clock size={16} /> Out of Commission
          </button>
          <button
            onClick={() => setStatus('pause')}
            disabled={statusLoading === 'pause'}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${!vendor?.is_active && !vendor?.out_of_commission ? 'bg-red-500/20 border-2 border-red-500/40 text-red-400' : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:border-red-500/30'}`}
            data-testid="status-pause"
          >
            <AlertCircle size={16} /> Pause Profile
          </button>
        </div>
        <p className="text-[#64748B] text-[10px] mt-3">
          {vendor?.out_of_commission
            ? 'Your profile is visible but marked as not taking new orders. Finish current orders to reactivate.'
            : vendor?.is_active !== false
            ? 'Your profile is live and visible to customers.'
            : 'Your profile is hidden from the marketplace.'}
        </p>
      </GlassCard>

      {/* Promo Code */}
      <GlassCard className="p-6" data-testid="promo-code-edit">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-white font-syne flex items-center gap-2">
            <Gift size={18} className="text-[#A78BFA]" /> Your Promo Code
          </h2>
          <AutoSaveIndicator status={promoSave.status} lastSaved={promoSave.lastSaved} />
        </div>
        <div className="flex-1">
          <input
            id="promo-edit"
            value={promoCode}
            placeholder="e.g. MYCODE123"
            maxLength={12}
            className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-lg font-mono tracking-wider p-3 uppercase"
            onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            data-testid="promo-code-input"
          />
          <p className="text-[#64748B] text-[10px] mt-1">
            Share this code to earn Reward Credits when people you refer transact. Saved automatically once you've typed at least 4 characters.
          </p>
        </div>
      </GlassCard>

      {/* Ethnicity gate banner */}
      {(!ethnicity || ethnicity.length === 0) && (
        <GlassCard className="p-4 border-[#F59E0B]/30 bg-[#F59E0B]/5" data-testid="ethnicity-gate">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-[#FCD34D] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[#FCD34D] font-bold text-sm">Set your ethnic background to start selling</p>
              <p className="text-[#9CA3AF] text-xs mt-1">We require this to showcase the diverse heritage of our vendor community. You won't be able to list services until this is set.</p>
            </div>
          </div>
        </GlassCard>
      )}

      <ServiceAreaAndVideo
        vendor={vendor}
        getAuthHeaders={getAuthHeaders}
        onChanged={fetchVendor}
      />

      {/* Audio Samples (musicians / producers / beat-makers) */}
      <GlassCard className="p-6">
        <AudioTracks />
      </GlassCard>

      {/* Profile Picture */}
      <GlassCard className={`p-6 ${vendor?.profile_image ? '' : 'border-[#F59E0B]/40 bg-[#F59E0B]/5'}`} data-testid="profile-picture-section">
        <h2 className="text-lg font-semibold text-white font-syne mb-2 flex items-center gap-2">
          Profile Picture {!vendor?.profile_image && <span className="text-[#FCD34D] text-xs font-normal">· REQUIRED</span>}
        </h2>
        {!vendor?.profile_image && (
          <p className="text-[#FCD34D]/90 text-xs mb-3" data-testid="profile-picture-required-warning">
            A profile photo is mandatory. Vendors without a profile picture are not listed in search results or on the homepage's "This Week's New Vendors" spotlight.
          </p>
        )}
        <div className="flex items-center gap-6">
          <div className={`w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0 ${vendor?.profile_image ? 'bg-white/5' : 'bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40'}`}>
            {vendor?.profile_image ? (
              <img src={vendor.profile_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${vendor.profile_image}` : vendor.profile_image} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#FCD34D]">
                <Upload size={32} />
              </div>
            )}
          </div>
          <div>
            <input type="file" accept="image/*" onChange={handleProfilePictureUpload} className="hidden" id="profile-picture-upload" data-testid="profile-picture-upload" />
            <label htmlFor="profile-picture-upload">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] cursor-pointer hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm">
                <Upload size={16} /> {uploading ? 'Uploading...' : (vendor?.profile_image ? 'Change Picture' : 'Upload Picture Now')}
              </span>
            </label>
            <p className="text-[#9CA3AF] text-xs mt-2">JPG, PNG, or GIF. Max 10MB.</p>
          </div>
        </div>
      </GlassCard>

      {/* Banner Image */}
      <GlassCard className="p-6" data-testid="banner-image-section">
        <h2 className="text-lg font-semibold text-white font-syne mb-2 flex items-center gap-2">
          <ImageIcon size={18} className="text-[#A78BFA]" /> Profile Banner
        </h2>
        <p className="text-[#9CA3AF] text-sm mb-4">A wide hero image displayed at the top of your public profile. Recommended: 1600×400.</p>
        <div className="relative w-full aspect-[4/1] rounded-xl overflow-hidden bg-gradient-to-br from-[#2D2D52]/80 to-[#1A1A3E] border border-white/10 mb-4">
          {vendor?.banner_image ? (
            <img
              src={vendor.banner_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${vendor.banner_image}` : vendor.banner_image}
              alt="Banner"
              className="w-full h-full object-cover"
              data-testid="banner-preview"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#64748B]">
              <div className="text-center">
                <ImageIcon size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-xs">No banner uploaded yet</p>
              </div>
            </div>
          )}
        </div>
        <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" id="banner-upload" data-testid="banner-upload" />
        <label htmlFor="banner-upload">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] cursor-pointer hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm">
            <Upload size={16} /> {uploadingBanner ? 'Uploading...' : vendor?.banner_image ? 'Replace Banner' : 'Upload Banner'}
          </span>
        </label>
        <p className="text-[#9CA3AF] text-xs mt-2">JPG or PNG. Max 15MB.</p>
      </GlassCard>

      {/* Business Info */}
      <GlassCard className="p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-white font-syne">Business Information</h2>
          <AutoSaveIndicator status={profileSave.status} lastSaved={profileSave.lastSaved} />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Business Name</Label>
          <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl h-11" data-testid="edit-business-name" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Business Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe what your business offers to clients…" className="bg-white/5 border-white/10 text-white rounded-xl min-h-[120px] placeholder:text-white/20" data-testid="edit-description" />
          <p className="text-[#64748B] text-xs mt-1">What you do, who you serve, and what makes your work distinctive.</p>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">About the Owner <span className="text-[#A78BFA]">(your story)</span></Label>
          <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Share your passion, heritage, and why you started this business…" className="bg-white/5 border-white/10 text-white rounded-xl min-h-[120px] placeholder:text-white/20" data-testid="edit-bio" />
          <p className="text-[#64748B] text-xs mt-1">Your personal story is what turns browsers into believers. Keep it authentic.</p>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            YouTube Featured Video <span className="text-[#64748B]">(optional)</span>
          </Label>
          <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=… or https://youtu.be/…" className="bg-white/5 border-white/10 text-white rounded-xl h-11" data-testid="edit-youtube-url" />
          <p className="text-[#64748B] text-xs mt-1">Paste any YouTube URL — it'll auto-embed as a video player on your profile.</p>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-1 block">Location (City, State only)</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Phoenix, Arizona" className="bg-white/5 border-white/10 text-white rounded-xl h-11" data-testid="edit-location" />
          <p className="text-[#64748B] text-xs mt-1">For your privacy, only share city & state — never your street address.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">
              Ethnic Background <span className="text-[#FCA5A5]">*</span>
              <span className="text-[#64748B] ml-1">(select all that apply)</span>
            </Label>
            <div className="mt-2">
              <EthnicityMultiSelect value={ethnicity} onChange={setEthnicity} testId="edit-ethnicity" compact />
            </div>
            <p className="text-[#64748B] text-xs mt-2">Required to list services. Shown on your public profile. Pick every background that represents you.</p>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block flex items-center gap-2">
              Tribal Affiliation
              <span className="text-[#64748B] text-[10px]">(Recommended for Indigenous vendors)</span>
            </Label>
            <Input
              value={tribalAffiliation}
              onChange={(e) => setTribalAffiliation(e.target.value)}
              placeholder={hasIndigenous(ethnicity) ? 'e.g., Navajo Nation, Cherokee, Lakota' : 'Only applies to Indigenous vendors'}
              className="bg-white/5 border-white/10 text-white rounded-xl h-11 placeholder:text-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="edit-tribal"
              disabled={!hasIndigenous(ethnicity)}
            />
            <p className="text-[#64748B] text-xs mt-1">
              {hasIndigenous(ethnicity)
                ? 'Optional but recommended — unlocks the tribal verified badge once reviewed.'
                : 'Select an Indigenous background above to enable.'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Age <span className="text-[#FCA5A5]">*</span></Label>
            <Input
              type="number"
              min={13}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="e.g., 32"
              className="bg-white/5 border-white/10 text-white rounded-xl h-11"
              data-testid="edit-age"
            />
            <p className="text-[#64748B] text-xs mt-1">Private — only Master reviewers can see this.</p>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Gender <span className="text-[#FCA5A5]">*</span></Label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl h-11 px-3 text-sm"
              data-testid="edit-gender"
            >
              <option value="" disabled className="bg-[#1A1A3E]">Select…</option>
              <option value="male" className="bg-[#1A1A3E]">Male</option>
              <option value="female" className="bg-[#1A1A3E]">Female</option>
              <option value="non_binary" className="bg-[#1A1A3E]">Non-Binary</option>
            </select>
            <p className="text-[#64748B] text-xs mt-1">Displayed publicly on your vendor card.</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="save-profile">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </GlassCard>

      {/* Business Registration / EIN */}
      <GlassCard className="p-6" data-testid="business-registration-card">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={20} className="text-[#A78BFA]" />
          <h2 className="text-lg font-semibold text-white font-syne">Business Registration (EIN)</h2>
          {vendor?.vendor_type === 'verified' ? (
            <span className="ml-auto px-3 py-1 rounded-full bg-green-500/15 text-[#86EFAC] border border-green-500/20 text-xs font-bold flex items-center gap-1">
              <CheckCircle size={12} /> Verified Business
            </span>
          ) : (
            <span className="ml-auto px-3 py-1 rounded-full bg-[#8B5CF6]/15 text-[#A78BFA] border border-[#8B5CF6]/20 text-xs font-bold">
              Startup Vendor
            </span>
          )}
        </div>
        <p className="text-[#9CA3AF] text-sm mb-4">
          Got your business officially registered after launching? Add your <strong className="text-white">EIN (Employer Identification Number)</strong> and we'll automatically upgrade your profile from <em>Startup</em> to <em>Verified Business</em> — customers see the trust badge immediately.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-[#9CA3AF] text-xs mb-1 block">EIN (9 digits)</Label>
            <Input
              value={einNumber}
              onChange={(e) => {
                // Allow typing with optional dash, max 10 chars
                const v = e.target.value.replace(/[^0-9-]/g, '').slice(0, 10);
                setEinNumber(v);
              }}
              placeholder="12-3456789"
              maxLength={10}
              className="bg-white/5 border-white/10 text-white rounded-xl h-11 font-mono tracking-wider"
              data-testid="edit-ein"
              disabled={vendor?.vendor_type === 'verified'}
            />
          </div>
          <button
            onClick={async () => {
              if (vendor?.vendor_type === 'verified') return;
              const cleaned = (einNumber || '').replace(/-/g, '');
              if (!cleaned || cleaned.length !== 9 || !/^\d{9}$/.test(cleaned)) {
                toast.error('EIN must be 9 digits (e.g., 12-3456789)');
                return;
              }
              setSaving(true);
              try {
                const res = await fetch(`${API}/vendors/me`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({ ein_number: einNumber.trim() }),
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                  if (data.vendor_type === 'verified' && vendor?.vendor_type !== 'verified') {
                    toast.success('🎉 Profile updated — you\'ve been promoted to Verified Business!', { duration: 6000 });
                  } else {
                    toast.success('EIN saved!');
                  }
                  setVendor(data);
                  setEinNumber(data.ein_number || '');
                } else {
                  toast.error(data?.detail || 'Failed to save EIN');
                }
              } catch (err) {
                toast.error('Failed to save EIN');
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving || vendor?.vendor_type === 'verified' || !einNumber}
            className="px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#10B981] to-[#059669] shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm whitespace-nowrap"
            data-testid="save-ein-btn"
          >
            {saving ? 'Saving…' : vendor?.vendor_type === 'verified' ? 'Verified ✓' : 'Verify & Save'}
          </button>
        </div>
        <p className="text-[#64748B] text-[10px] mt-2">
          {vendor?.vendor_type === 'verified'
            ? 'EIN on file. Contact support to change it.'
            : 'Your EIN is kept private. We use it only to verify your business registration.'}
        </p>
      </GlassCard>

      {/* Portfolio */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-2">Portfolio (Work Samples)</h2>
        <p className="text-[#9CA3AF] text-sm mb-6">Upload images and videos to showcase your work.</p>
        <div className="flex gap-3 mb-6">
          <div>
            <input type="file" accept="image/*" onChange={(e) => handlePortfolioUpload(e, 'image')} className="hidden" id="portfolio-image-upload" data-testid="portfolio-image-upload" />
            <label htmlFor="portfolio-image-upload">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] cursor-pointer text-sm transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]">
                {uploading ? 'Uploading...' : 'Upload Image'}
              </span>
            </label>
          </div>
          <div>
            <input type="file" accept="video/mp4,video/quicktime,video/webm,video/*" onChange={(e) => handlePortfolioUpload(e, 'video')} className="hidden" id="portfolio-video-upload" data-testid="portfolio-video-upload" />
            <label htmlFor="portfolio-video-upload">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-[#9CA3AF] border border-white/10 cursor-pointer text-sm hover:text-white hover:bg-white/5 transition-all">
                {uploading ? 'Uploading...' : 'Upload Video'}
              </span>
            </label>
          </div>
        </div>
        {vendor?.portfolio_images?.length > 0 || vendor?.portfolio_videos?.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {vendor.portfolio_images?.map((img, index) => {
                const mediaId = extractMediaId(img);
                const src = img.startsWith('http')
                  ? img
                  : img.startsWith('/api/')
                  ? `${process.env.REACT_APP_BACKEND_URL}${img}`
                  : `${process.env.REACT_APP_BACKEND_URL}/api/users/media/${img}`;
                return (
                  <div key={`img-${index}`} className="relative aspect-square rounded-xl overflow-hidden bg-white/5 group" data-testid={`portfolio-img-${index}`}>
                    <img src={src} alt={`Portfolio ${index + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 top-0 p-2 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
                      <div className="flex items-center gap-1">
                        <button onClick={() => movePortfolioImage(index, -1)} disabled={index === 0} className="p-1 bg-black/50 rounded text-white hover:bg-black/80 disabled:opacity-30" data-testid={`move-left-${index}`}>
                          <ArrowLeft size={12} />
                        </button>
                        <span className="text-white text-[10px] font-mono bg-black/40 px-1.5 py-0.5 rounded">{index + 1}</span>
                        <button onClick={() => movePortfolioImage(index, 1)} disabled={index === vendor.portfolio_images.length - 1} className="p-1 bg-black/50 rounded text-white hover:bg-black/80 disabled:opacity-30" data-testid={`move-right-${index}`}>
                          <ArrowRight size={12} />
                        </button>
                      </div>
                      <button onClick={() => handleDeletePortfolioMedia(mediaId)} className="p-1.5 bg-red-500 rounded-lg text-white hover:bg-red-600 transition-all shadow-lg" data-testid={`delete-portfolio-${index}`}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {vendor.portfolio_videos?.map((video, index) => {
                const mediaId = extractMediaId(video);
                const src = video.startsWith('http')
                  ? video
                  : video.startsWith('/api/')
                  ? `${process.env.REACT_APP_BACKEND_URL}${video}`
                  : `${process.env.REACT_APP_BACKEND_URL}/api/users/media/${video}`;
                return (
                  <div key={`vid-${index}`} className="relative aspect-square rounded-xl overflow-hidden bg-black/40">
                    <video src={src} controls playsInline className="w-full h-full object-cover" preload="metadata" data-testid={`portfolio-video-${index}`} />
                    <div className="absolute inset-x-0 top-0 p-2 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
                      <span className="text-white text-[10px] font-mono bg-black/40 px-1.5 py-0.5 rounded">Video {index + 1}</span>
                      <button onClick={() => handleDeletePortfolioMedia(mediaId)} className="p-1.5 bg-red-500 rounded-lg text-white hover:bg-red-600 transition-all shadow-lg pointer-events-auto" data-testid={`delete-video-${index}`}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[#64748B] text-xs text-center">Use arrow buttons to reorder. Click the red X to delete.</p>
          </div>
        ) : (
          <div className="text-center py-8 bg-white/5 rounded-xl">
            <Package size={32} className="mx-auto mb-3 text-[#9CA3AF] opacity-40" />
            <p className="text-[#9CA3AF] text-sm">No portfolio items yet. Upload images or videos of your work!</p>
          </div>
        )}
      </GlassCard>

      {/* Identity Verification — handled by Stripe Connect */}
      <GlassCard className="p-6 border-[#8B5CF6]/20" data-testid="identity-verification-card">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={20} className="text-[#A78BFA]" />
          <h2 className="text-lg font-semibold text-white font-syne">Identity Verification</h2>
        </div>

        <div className="p-4 rounded-xl bg-white/5 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[#9CA3AF] text-sm">Status:</span>
            {stripeStatus?.connected ? (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/15 text-[#86EFAC] text-xs font-semibold border border-green-500/20" data-testid="stripe-verified-badge">
                <CheckCircle size={14} /> Verified via Stripe
              </span>
            ) : stripeStatus?.has_account ? (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 text-[#FCD34D] text-xs font-semibold border border-yellow-500/20" data-testid="stripe-pending-badge">
                <Clock size={14} /> Setup Incomplete
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-500/15 text-[#9CA3AF] text-xs font-semibold border border-zinc-500/20" data-testid="stripe-not-started-badge">
                <AlertCircle size={14} /> Not Verified
              </span>
            )}
          </div>
        </div>

        <p className="text-[#D1D5DB] text-sm leading-relaxed mb-4">
          To sell services or products on EntrepreNatives, identity verification is required before payouts can be released.
          We don&apos;t store your ID — verification is handled securely by <span className="text-[#A78BFA] font-bold">Stripe</span>,
          our payments partner. This protects buyers from fraud and ensures vendors are accountable for completed work.
        </p>

        <p className="text-[#9CA3AF] text-xs leading-relaxed mb-5">
          You&apos;ll be redirected to Stripe to upload a government-issued ID and link a payout account.
          Stripe stores and reviews your documents directly under their bank-grade security.
        </p>

        {stripeStatus?.connected ? (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
            <CheckCircle size={20} className="text-[#86EFAC]" />
            <div>
              <p className="text-[#86EFAC] font-bold text-sm">Identity Verified</p>
              <p className="text-[#9CA3AF] text-xs">Verified by Stripe. Payouts are enabled.</p>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleConnectStripe}
            disabled={connectingStripe}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_28px_rgba(139,92,246,0.5)] disabled:opacity-60 transition-all text-sm"
            data-testid="verify-with-stripe-btn"
          >
            <Shield size={16} />
            {connectingStripe
              ? 'Redirecting…'
              : stripeStatus?.has_account
                ? 'Continue Stripe Setup'
                : 'Verify Identity with Stripe'}
          </button>
        )}
      </GlassCard>

      {/* Sign Out */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-3">Account</h2>
        <button
          onClick={async () => {
            await logout();
            navigate('/');
          }}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-[#9CA3AF] border border-white/10 hover:text-white hover:bg-white/5 transition-all"
          data-testid="sign-out-btn"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </GlassCard>

      {/* Danger Zone */}
      <GlassCard className="p-6 border-red-500/20">
        <h2 className="text-lg font-semibold text-[#FCA5A5] font-syne mb-4">Danger Zone</h2>
        <p className="text-[#9CA3AF] text-sm mb-4">Permanently delete your account and all associated data. We'll ask 3 quick questions before proceeding. <strong>You cannot delete while you have open jobs.</strong></p>
        <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 rounded-xl text-sm font-medium text-[#FCA5A5] border border-red-500/30 hover:bg-red-500/10 transition-all" data-testid="delete-account-btn">
          <Trash2 size={14} className="inline mr-2" /> Delete Account
        </button>
      </GlassCard>
      <DeleteAccountModal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} />
    </div>
  );
}
